// 반품 상세 본문. 전체 페이지(/cases/:id)와 목록의 옆판(Drawer)이 이 하나를 같이 쓴다.
// 두 벌로 두면 한쪽만 고쳐져 "페이지에서는 되는데 팝업에서는 안 되는" 차이가 생긴다.
import { useCallback } from "react"
import type { CaseDetailResponse, CaseEvent } from "../../api/types.ts"
import { useApiQuery, useAsyncAction } from "../../hooks/index.ts"
import { useT } from "../../i18n/index.tsx"
import { useSession } from "../../auth/SessionProvider.tsx"
import { caseSteps, isTerminalCase } from "../../lib/case-steps.ts"
import { Alert, ErrorNote, Skeleton, Stepper, Tabs } from "../../ui/index.ts"
import { CaseHeader } from "./CaseHeader.tsx"
import { NextActions } from "./NextActions.tsx"
import { SummaryTab } from "./SummaryTab.tsx"
import { ItemsTab } from "./ItemsTab.tsx"
import { HistoryTab } from "./HistoryTab.tsx"

export function CaseDetailView({
  caseId,
  onChanged,
  showHeading = true,
}: {
  caseId: number
  onChanged?: () => void
  /** 옆판은 제 헤더에 건 번호를 이미 띄우므로 본문 제목을 끈다. */
  showHeading?: boolean
}) {
  const t = useT()
  const { user } = useSession()

  const detail = useApiQuery<CaseDetailResponse>(`/api/cases/${caseId}`)
  const history = useApiQuery<{ items: CaseEvent[] }>(`/api/cases/${caseId}/events`)
  const action = useAsyncAction()

  const reload = useCallback(async () => {
    await Promise.all([detail.reload(), history.reload()])
  }, [detail, history])

  /** 어떤 동작이든 끝나면 다시 읽는다 — 실패했어도 그렇다. 다른 쪽이 먼저 고쳤을 수 있다. */
  const run = useCallback(
    async (call: () => Promise<unknown>) => {
      const ok = await action.run(call)
      await reload()
      if (ok) onChanged?.()
    },
    [action, onChanged, reload],
  )

  const error = action.error ?? detail.error ?? history.error
  if (error && !detail.data) return <ErrorNote error={error} />
  if (!detail.data || !history.data || !user) return <Skeleton lines={6} />

  const data = detail.data
  const item = data.case
  // 인보이스 품목은 실물을 들고 있는 쿨트랙JP가 적는다(server/src/domain/permissions.ts).
  const isWarehouse = user.role === "warehouse" || user.role === "admin"

  return (
    <>
      <CaseHeader item={item} showHeading={showHeading} />

      {isTerminalCase(item.status) ? (
        <Alert tone="info">{t("caseDetail.terminal")}</Alert>
      ) : (
        <Stepper
          ariaLabel={t("caseDetail.title")}
          steps={caseSteps(item.status).map((step) => ({
            ...step,
            label: t(`caseDetail.step.${step.id}`),
          }))}
        />
      )}

      <ErrorNote error={error} />

      <NextActions caseId={caseId} data={data} busy={action.busy} run={run} />

      <Tabs
        ariaLabel={t("caseDetail.title")}
        items={[
          {
            id: "summary",
            label: t("caseDetail.tab.summary"),
            content: <SummaryTab item={item} lines={data.lines} />,
          },
          {
            id: "items",
            label: t("caseDetail.tab.items"),
            badge: data.items.length > 0 ? data.items.length : undefined,
            content: (
              <ItemsTab
                item={item}
                items={data.items}
                editable={isWarehouse && item.status === "arrived"}
                onSaved={() => {
                  void reload()
                  onChanged?.()
                }}
              />
            ),
          },
          {
            id: "history",
            label: t("caseDetail.tab.history"),
            content: <HistoryTab events={history.data.items} />,
          },
        ]}
      />
    </>
  )
}
